import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useT } from "@/i18n/I18nContext";
import { getNonconformities, downloadNonconformitiesXlsx, updateNonconformity, fmt } from "@/lib/api";
import { ShieldAlert, ClipboardCheck, AlertTriangle, Download, CheckCircle2, MoreHorizontal, Eye, Check, EyeOff, RotateCcw, Pencil } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";
const STATUS_FILTERS = ["active", "open", "acknowledged", "resolved", "dismissed", "all"];
const statusCls = {
  open: "badge-bad",
  acknowledged: "badge-warn",
  resolved: "badge-ok",
  dismissed: "bg-secondary text-muted-foreground border border-border",
};

/** Dialog used for "resolve" (corrective required), "edit corrective" and "dismiss" (reason). */
function NcrActionDialog({ action, item, onClose, onSaved }) {
  const { t, td } = useT();
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!item) return;
    setText(item.corrective || "");
    setNote(item.ncr_note || "");
  }, [item, action]);
  if (!item) return null;
  const isDismiss = action === "dismiss";
  const isResolve = action === "resolve";
  const save = async () => {
    if (isResolve && !text.trim()) {
      toast.error(t("ncr.correctiveRequired"));
      return;
    }
    if (isDismiss && !note.trim()) {
      toast.error(t("ncr.reasonRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = { field: item.field };
      if (isDismiss) { payload.status = "dismissed"; payload.note = note; }
      else if (isResolve) { payload.status = "resolved"; payload.corrective = text; if (note !== (item.ncr_note || "")) payload.note = note; }
      else { payload.corrective = text; }
      await updateNonconformity(item.production_id, payload);
      toast.success(t(isDismiss ? "ncr.dismissed" : isResolve ? "ncr.resolvedToast" : "ncr.correctiveSaved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ncr.saveError"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ncr-action-dialog">
        <DialogHeader>
          <DialogTitle>{t(isDismiss ? "ncr.dismissTitle" : isResolve ? "ncr.resolveTitle" : "ncr.editCorrectiveTitle")}</DialogTitle>
          <DialogDescription>
            {item.date} · {item.lot || "—"} · {td(item.product)} · <b>{item.field}</b>{typeof item.value === "number" ? ` = ${fmt(item.value, 2)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isDismiss && (
            <div className="space-y-1.5">
              <Label>{t("prod.correctiveAction")}{isResolve ? " *" : ""}</Label>
              <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("ncr.correctivePlaceholder")} data-testid="ncr-corrective-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{isDismiss ? `${t("ncr.dismissReason")} *` : t("ncr.noteOpt")}</Label>
            <Textarea rows={isDismiss ? 3 : 2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={isDismiss ? t("ncr.dismissPlaceholder") : ""} data-testid="ncr-note-input" />
          </div>
          {isDismiss && <p className="text-xs text-muted-foreground">{t("ncr.dismissHint")}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} variant={isDismiss ? "secondary" : "default"} data-testid="ncr-action-save">
            {t(isDismiss ? "ncr.dismissBtn" : isResolve ? "ncr.resolveBtn" : "common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Audits usually cover a quarter — default to the last 3 months.
const threeMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function Nonconformities() {
  const { t, td } = useT();
  const [start, setStart] = useState(threeMonthsAgo());
  const [end, setEnd] = useState(today());
  const [product, setProduct] = useState(ALL);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [dialog, setDialog] = useState(null); // { action, item }

  const quickStatus = async (it, status) => {
    try {
      await updateNonconformity(it.production_id, { field: it.field, status });
      toast.success(t(status === "acknowledged" ? "ncr.acknowledged" : "ncr.reopened"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ncr.saveError"));
    }
  };

  const params = useMemo(() => ({
    start, end,
    ...(product === ALL ? {} : { product }),
  }), [start, end, product]);

  const load = useCallback(() => {
    setLoading(true);
    getNonconformities(params)
      .then((d) => {
        setData(d);
        if (product === ALL) setProducts(d.products || []);
      })
      .catch(() => { setData(null); toast.error(t("ncr.loadError")); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadNonconformitiesXlsx(params);
    } catch {
      toast.error(t("losses.exportError"));
    } finally {
      setExporting(false);
    }
  };

  // Warning issue strings come from the QC engine in English — localize the known patterns.
  const issueLabel = (s) => {
    if (s === "non-conforming option") return t("ncr.issueFailOption");
    if (s.startsWith("below min")) return s.replace("below min", t("ncr.belowMin"));
    if (s.startsWith("above max")) return s.replace("above max", t("ncr.aboveMax"));
    return s;
  };

  const totals = data?.totals || { total: 0, with_corrective: 0, missing_corrective: 0, open: 0, acknowledged: 0, resolved: 0, dismissed: 0 };
  const allItems = data?.items || [];
  const items = useMemo(() => allItems.filter((it) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return it.ncr_status !== "dismissed";
    return it.ncr_status === statusFilter;
  }), [allItems, statusFilter]);

  return (
    <Layout
      actions={
        <Button size="sm" variant="outline" onClick={onExport} disabled={exporting || loading}
          data-testid="ncr-export-xlsx" className="gap-1.5">
          <Download className="h-4 w-4" /> {t("losses.exportXlsx")}
        </Button>
      }
    >
      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--danger))]" /> {t("ncr.title")}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("ncr.subtitle")}</p>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.from")}</Label>
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)}
              className="h-9 w-[160px] text-sm" data-testid="ncr-start" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.to")}</Label>
            <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
              className="h-9 w-[160px] text-sm" data-testid="ncr-end" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("common.product")}</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="ncr-product-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("stockMovements.allProducts")}</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{td(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("ncr.statusCol")}</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[190px] text-sm" data-testid="ncr-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((k) => <SelectItem key={k} value={k}>{t(`ncr.filter_${k}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Card data-testid="ncr-card-open">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("ncr.openCard")}
            </div>
            <div className={`mt-2 font-mono text-2xl font-semibold tabular-nums ${totals.open > 0 ? "text-[hsl(var(--danger))]" : ""}`} data-testid="ncr-open">
              {totals.open}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="ncr-card-resolved">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> {t("ncr.resolvedCard")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums" data-testid="ncr-resolved">
              {totals.resolved}<span className="ml-1 text-sm font-normal text-muted-foreground">/ {totals.total}</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="ncr-card-total">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldAlert className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("ncr.totalCard")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums" data-testid="ncr-total">
              {totals.total}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="ncr-card-with">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ClipboardCheck className="h-4 w-4 text-[hsl(var(--success))]" /> {t("ncr.withCorrective")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums text-[hsl(var(--success))]" data-testid="ncr-with-corrective">
              {totals.with_corrective}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="ncr-card-missing">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("ncr.missingCorrective")}
            </div>
            <div className={`mt-2 font-mono text-2xl font-semibold tabular-nums ${totals.missing_corrective > 0 ? "text-[hsl(var(--warning))]" : ""}`}
              data-testid="ncr-missing-corrective">
              {totals.missing_corrective}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Register table */}
      <Card className="mt-4">
        <CardContent className="pt-5">
          <div className="overflow-x-auto thin-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stockMovements.date")}</TableHead>
                  <TableHead>{t("stockMovements.lot")}</TableHead>
                  <TableHead>{t("common.product")}</TableHead>
                  <TableHead>{t("ncr.checkCol")}</TableHead>
                  <TableHead className="text-right">{t("prod.value")}</TableHead>
                  <TableHead>{t("ncr.issueCol")}</TableHead>
                  <TableHead>{t("prod.correctiveAction")}</TableHead>
                  <TableHead>{t("ncr.operatorCol")}</TableHead>
                  <TableHead>{t("ncr.statusCol")}</TableHead>
                  <TableHead className="w-[48px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground" data-testid="ncr-empty">
                      <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[hsl(var(--success))]/50" />
                      {t("ncr.empty")}
                    </TableCell>
                  </TableRow>
                ) : items.map((it, idx) => (
                  <TableRow key={`${it.production_id}-${it.field}-${idx}`} data-testid={`ncr-row-${idx}`} className={it.ncr_status === "dismissed" ? "opacity-60" : ""}>
                    <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{it.date}</TableCell>
                    <TableCell className="font-mono text-xs">{it.lot || "—"}</TableCell>
                    <TableCell className="font-medium">{td(it.product)}</TableCell>
                    <TableCell>{it.field}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {typeof it.value === "number" ? fmt(it.value, 2) : it.value}
                    </TableCell>
                    <TableCell>
                      <Badge className="badge-bad border-transparent text-[10px]" data-testid={`ncr-issue-${idx}`}>
                        {issueLabel(it.issue)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[280px]">
                      {it.corrective ? (
                        <span className="whitespace-normal text-xs" data-testid={`ncr-corrective-${idx}`}>{it.corrective}</span>
                      ) : (
                        <Badge variant="outline" className="border-[hsl(var(--warning))] text-[10px] text-[hsl(var(--warning))]" data-testid={`ncr-corrective-missing-${idx}`}>
                          {t("ncr.missing")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate text-xs text-muted-foreground">{it.operator || "—"}</TableCell>
                    <TableCell>
                      <Badge className={`${statusCls[it.ncr_status] || statusCls.open} border-transparent text-[10px]`} data-testid={`ncr-status-${idx}`}>
                        {t(`ncr.status_${it.ncr_status || "open"}`)}
                      </Badge>
                      {it.ncr_by && (
                        <div className="mt-0.5 text-[10px] text-muted-foreground" title={it.ncr_note || ""}>
                          {it.ncr_by} · {(it.ncr_at || "").slice(0, 10)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={t("common.actions")} data-testid={`ncr-actions-${idx}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          {it.ncr_status === "open" && (
                            <DropdownMenuItem onClick={() => quickStatus(it, "acknowledged")} data-testid={`ncr-ack-${idx}`}>
                              <Eye className="mr-2 h-4 w-4" /> {t("ncr.actAcknowledge")}
                            </DropdownMenuItem>
                          )}
                          {it.ncr_status !== "resolved" && it.ncr_status !== "dismissed" && (
                            <DropdownMenuItem onClick={() => setDialog({ action: "resolve", item: it })} data-testid={`ncr-resolve-${idx}`}>
                              <Check className="mr-2 h-4 w-4" /> {t("ncr.actResolve")}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setDialog({ action: "edit", item: it })} data-testid={`ncr-edit-${idx}`}>
                            <Pencil className="mr-2 h-4 w-4" /> {it.corrective ? t("ncr.actEditCorrective") : t("ncr.actAddCorrective")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {it.ncr_status !== "dismissed" ? (
                            <DropdownMenuItem onClick={() => setDialog({ action: "dismiss", item: it })} data-testid={`ncr-dismiss-${idx}`}>
                              <EyeOff className="mr-2 h-4 w-4" /> {t("ncr.actDismiss")}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => quickStatus(it, "open")} data-testid={`ncr-reopen-${idx}`}>
                              <RotateCcw className="mr-2 h-4 w-4" /> {t("ncr.actReopen")}
                            </DropdownMenuItem>
                          )}
                          {it.ncr_status === "resolved" && (
                            <DropdownMenuItem onClick={() => quickStatus(it, "open")} data-testid={`ncr-reopen-${idx}`}>
                              <RotateCcw className="mr-2 h-4 w-4" /> {t("ncr.actReopen")}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {items.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">{t("ncr.tableNote")}</p>
          )}
        </CardContent>
      </Card>
      <NcrActionDialog
        action={dialog?.action}
        item={dialog?.item || null}
        onClose={() => setDialog(null)}
        onSaved={() => { setDialog(null); load(); }}
      />
    </Layout>
  );
}
